/**
 * Confirmation modal for promoting a winning split-test variant; calls
 * JourneyService.promoteVariant (via onConfirm) and refreshes the journey.
 */

import React, { useState } from 'react'
import { Alert, Modal } from 'antd'
import type { ButtonProps } from 'antd'
import { CheckCircleOutlined, RiseOutlined, WarningOutlined } from '@ant-design/icons'
import './promote-variant-modal.scss'

// ─── Props ────────────────────────────────────────────────────────────────────

export type PromoteVariantModalProps = {
    open: boolean
    /** Human-readable variant name, e.g. "Every 7 Days" */
    variantName: string
    /** Number of OTHER variants being removed, used in bullet copy */
    otherVariantCount: number
    /** Called when the user confirms; should call the API */
    onConfirm: () => Promise<void>
    /** Called when the user cancels or closes */
    onCancel: () => void
}

// ─── Component ───────────────────────────────────────────────────────────────

export const PromoteVariantModal: React.FC<PromoteVariantModalProps> = ({
    open,
    variantName,
    otherVariantCount,
    onConfirm,
    onCancel,
}) => {
    const [confirming, setConfirming] = useState(false)

    const handleOk = async () => {
        setConfirming(true)
        try {
            await onConfirm()
        } finally {
            setConfirming(false)
        }
    }

    const otherLabel =
        otherVariantCount === 1
            ? 'The other variant is'
            : `The other ${otherVariantCount} variants are`

    return (
        <Modal
            className="promote-variant-modal"
            open={open}
            title={`Promote "${variantName}"?`}
            okText="Promote This Variant"
            cancelText="Cancel"
            okButtonProps={{
                type: 'primary',
                icon: <RiseOutlined aria-hidden />,
                loading: confirming,
                disabled: confirming,
                'data-testid': 'promote-confirm-btn',
            } as ButtonProps & { 'data-testid'?: string }}
            cancelButtonProps={{ disabled: confirming }}
            onOk={handleOk}
            onCancel={onCancel}
            maskClosable={!confirming}
            closable={!confirming}
            footer={(_, { OkBtn, CancelBtn }) => (
                <div className="promote-variant-modal-footer">
                    {/* Actions right-aligned to match the app's other modals. */}
                    <div className="promote-variant-modal-footer-actions">
                        <CancelBtn />
                        <OkBtn />
                    </div>
                </div>
            )}
        >
            <p className="promote-variant-modal-intro">
                Promoting ends this split test and makes the &ldquo;{variantName}&rdquo; path the
                journey&rsquo;s single flow. Here&rsquo;s exactly what happens:
            </p>

            <ul className="promote-variant-modal-bullets">
                <li>
                    <CheckCircleOutlined className="bullet-check" aria-hidden />
                    <span>New entrants follow the &ldquo;{variantName}&rdquo; path from now on.</span>
                </li>
                <li>
                    <CheckCircleOutlined className="bullet-check" aria-hidden />
                    <span>
                        Subscribers currently in another variant finish the path they&rsquo;re already
                        on &mdash; no one is moved onto the winning path or dropped from the journey.
                    </span>
                </li>
                <li>
                    <CheckCircleOutlined className="bullet-check" aria-hidden />
                    <span>
                        {otherLabel} removed from the active journey. The original split and its
                        per-variant metrics stay viewable in revision history.
                    </span>
                </li>
            </ul>

            <Alert
                className="promote-variant-modal-warning"
                type="warning"
                icon={<WarningOutlined aria-hidden />}
                showIcon
                description={
                    <>
                        This can&rsquo;t be undone. There&rsquo;s no way to un-promote or restore the
                        split once you confirm. The change is customer-facing and takes effect
                        immediately on this running journey.
                    </>
                }
            />
        </Modal>
    )
}
